import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { AddToCalendarButton } from "add-to-calendar-button-react";
import { useGetCalendarDetailsMutation } from "../services/findTournamentApi";
import { ContentSection, FlexColumn, FlexRow, Text } from "../styles/Global";
import * as Global from "../styles/Global";
import { FaMapMarkerAlt, FaClock } from "react-icons/fa";
import SHCoatOfArms from "../assets/SH-COA.png";
import moment from "moment";
import SkeletonLoader from "./SkeletonLoader";
import { setGlobal } from "../features/global/globalSlice";
import DescriptionExpander from "./DescriptionExpander";

// mirrors add-to-calendar-button's round icon-only spec (.atcb-no-text): 3em circle → 3.26em on hover
const AppleCalendarLink = styled.a`
  display: flex;
  place-content: center center;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  width: 3em;
  height: 3em;
  margin: 0.13em;
  border-radius: 100%;
  cursor: pointer;
  background-color: ${(props) => (props.$darkMode ? "#2e2e2e" : "#f5f5f5")};
  border: 1px solid ${(props) => (props.$darkMode ? "#4d4d4d" : "#d2d2d2")};
  box-shadow: ${(props) =>
    props.$darkMode
      ? "rgb(255 255 255 / 3%) -3px -3px 34px -1px, rgb(0 0 0 / 10%) 2px 3px 14px -2px, rgb(0 0 0 / 12%) 1px 2px 8px -1px"
      : "rgb(0 0 0 / 10%) 0 4px 14px -2px, rgb(0 0 0 / 12%) 0 2px 6px -1px"};
  svg {
    width: 1em;
    height: 1em;
    fill: ${(props) => (props.$darkMode ? "#dedede" : "#333")};
  }
  &:hover {
    width: 3.26em;
    height: 3.26em;
    margin: 0;
    background-color: ${(props) => (props.$darkMode ? "#373737" : "#fff")};
    border-color: ${(props) => (props.$darkMode ? "#373737" : "#fff")};
    box-shadow: ${(props) =>
      props.$darkMode
        ? "rgb(0 0 0 / 18%) 2px 5px 24px -4px, rgb(0 0 0 / 14%) 1px 2px 10px -2px"
        : "rgb(0 0 0 / 16%) 0 5px 18px -3px, rgb(0 0 0 / 14%) 0 3px 8px -2px"};
    svg {
      fill: ${(props) => (props.$darkMode ? "#f1f1f1" : "#000")};
    }
  }
`;

const AppleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 245.657">
    <path d="M167.084 130.514c-.308-31.099 25.364-46.022 26.511-46.761-14.429-21.107-36.91-24.008-44.921-24.335-19.13-1.931-37.323 11.27-47.042 11.27-9.692 0-24.67-10.98-40.532-10.689-20.849.308-40.07 12.126-50.818 30.799-21.661 37.581-5.54 93.281 15.572 123.754 10.313 14.923 22.612 31.688 38.764 31.089 15.549-.612 21.433-10.073 40.242-10.073s24.086 10.073 40.546 9.751c16.737-.308 27.34-15.214 37.585-30.187 11.855-17.318 16.714-34.064 17.009-34.925-.372-.168-32.635-12.525-32.962-49.68l.045-.013zm-30.917-91.287C144.735 28.832 150.524 14.402 148.942 0c-12.344.503-27.313 8.228-36.176 18.609-7.956 9.216-14.906 23.904-13.047 38.011 13.786 1.075 27.862-7.004 36.434-17.376z" />
  </svg>
);

const AddToCalendar = () => {
  const dispatch = useDispatch();
  const globalDarkMode = useSelector((state) => state.global.darkMode);
  const urlParams = new URLSearchParams(window.location.search);
  const darkModeParam = urlParams.get("darkmode");
  const darkMode =
    darkModeParam !== null ? darkModeParam === "true" : globalDarkMode;
  const [eventData, setEventData] = useState(null);
  const [getCalendarDetails, { data: calendarData, isLoading, error }] =
    useGetCalendarDetailsMutation();

  // Set global dark mode if URL param is present
  useEffect(() => {
    if (darkModeParam !== null) {
      const isDark = darkModeParam === "true";
      if (isDark !== globalDarkMode) {
        dispatch(setGlobal({ darkMode: isDark }));
      }
    }
  }, [darkModeParam, globalDarkMode, dispatch]);

  // Get gameid from URL
  const gameid = window.location.pathname.split("/").pop();

  useEffect(() => {
    if (gameid) {
      console.log("getting calendar details for gameid:", gameid);
      getCalendarDetails({ gameid });
    }
  }, [gameid]);

  useEffect(() => {
    console.log("calendarData changed:", calendarData);
    if (calendarData?.data) {
      const data = calendarData.data;
      setEventData(data);
    }
  }, [calendarData]);

  // Format display date and time
  const formatDateTime = () => {
    if (!eventData) return { date: "", startTime: "", endTime: "" };

    const dateObj = moment(eventData.startDate);
    const formattedDate = dateObj.format("ddd, DD MMM YYYY");
    const formattedStartTime = moment(eventData.startTime, "HH:mm").format(
      "h:mm A"
    );
    const formattedEndTime = moment(eventData.endTime, "HH:mm").format(
      "h:mm A"
    );

    return {
      date: formattedDate,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    };
  };

  const { date, startTime, endTime } = formatDateTime();

  console.log("AddToCalendar eventData:", eventData);

  // Listen for Web Component errors
  useEffect(() => {
    const handleError = (e) => {
      console.error("AddToCalendar Web Component error:", e);
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  if (isLoading || !eventData) {
    return (
      <>
        <ContentSection style={{ flex: 0, margin: "0 7px" }}>
          <SkeletonLoader />
        </ContentSection>
        <ContentSection style={{ flex: 0, margin: "10px 7px" }}>
          <SkeletonLoader array={[{ h: 50, w: "100%" }]} />
        </ContentSection>
      </>
    );
  }

  if (error) {
    return (
      <FlexColumn
        style={{
          width: "100%",
          margin: "0 auto",
          padding: "20px",
        }}
      >
        <Text>Error loading event details</Text>
      </FlexColumn>
    );
  }

  return (
    <FlexColumn
      style={{
        width: "100%",
        margin: "0 auto",
        padding: "20px",
        gap: "10px",
      }}
    >
      {/* Event Title */}
      <ContentSection
        style={{
          flex: 0,
          padding: 20,
        }}
      >
        <FlexRow
          justify="center"
          align="center"
          style={{
            width: "100%",
            marginBottom: 20,
          }}
        >
          <Text
            size="1.8em"
            style={{
              fontWeight: "700",
              textAlign: "center",
            }}
          >
            {eventData.name}
          </Text>
        </FlexRow>

        {/* Event Image */}
        <FlexRow
          justify="flex-start"
          align="flex-start"
          style={{
            width: "100%",
            marginBottom: 20,
          }}
        >
          <img
            src={eventData.locationImage || SHCoatOfArms}
            onError={(e) => {
              e.target.src = SHCoatOfArms;
            }}
            alt="event logo"
            style={{
              maxHeight: 100,
              maxWidth: "100%",
              objectFit: "contain",
              borderRadius: 10,
            }}
          />
          <FlexColumn style={{ marginLeft: 10 }}>
            {/* Date & Time */}
            <FlexRow
              justify="flex-start"
              align="center"
              style={{
                width: "100%",
                marginBottom: 10,
              }}
            >
              <div style={{ width: 25 }}>
                <FaClock size={25} color={Global.redLight} />
              </div>
              <Text size=".9em" style={{ fontWeight: "600", marginLeft: 10 }}>
                {date}, {startTime} - {endTime}
              </Text>
            </FlexRow>

            {/* Location */}
            <FlexRow
              justify="flex-start"
              align="center"
              style={{
                width: "100%",
              }}
            >
              <div style={{ width: 25 }}>
                <FaMapMarkerAlt size={25} color={Global.redLight} />
              </div>
              <Text size=".9em" style={{ marginLeft: 10 }}>
                {eventData.location}
              </Text>
            </FlexRow>
          </FlexColumn>
        </FlexRow>

        {/* Description */}
        <DescriptionExpander
          description={eventData.description}
          minHeight={50}
          data={eventData}
          gradientColor={darkMode ? "#1F2A34" : "#FFFFFF"}
        />
      </ContentSection>

      {/* Add to Calendar Section */}
      <ContentSection
        style={{
          flex: 0,
          padding: 20,
        }}
      >
        <FlexRow
          justify="center"
          align="center"
          style={{
            width: "100%",
            marginBottom: 15,
          }}
        >
          <Text size="1.2em" style={{ fontWeight: "600" }}>
            Add to Calendar
          </Text>
        </FlexRow>

        <FlexRow
          justify="center"
          align="center"
          style={{
            width: "100%",
            gap: "5px",
            flexWrap: "wrap",
          }}
        >
          {(() => {
            // Build description with optional shareLink
            let description =
              eventData?.description !== ""
                ? eventData.description
                : "No Description Provided";

            if (eventData.location && eventData.location !== "") {
              description = `${eventData.location}[br][br] ${description} `;
            }

            if (eventData.shareLink && eventData.shareLink !== "") {
              description = `[url]${eventData.shareLink}[/url][br][br] ${description} `;
            }

            const buttonProps = {
              name: eventData.name,
              description: description,
              startDate: eventData.startDate,
              startTime: eventData.startTime,
              endTime: eventData.endTime,
              timeZone: eventData.timeZoneString,
              location: eventData.location,
              shareLink: eventData.shareLink,
              // hosted file: iOS in-app browsers silently drop the client-generated ics download
              icsFile: `${process.env.REACT_APP_API_BASE_URL}/apiv2/find/calendar/ics/${gameid}`,
            };

            console.log("AddToCalendarButton props:", buttonProps);
            console.log("Date format check:", {
              startDateValid: /^\d{4}-\d{2}-\d{2}$/.test(buttonProps.startDate),
              startTimeValid: /^\d{2}:\d{2}$/.test(buttonProps.startTime),
              endTimeValid: /^\d{2}:\d{2}$/.test(buttonProps.endTime),
              timeZone: buttonProps.timeZoneString,
            });

            return (
              <>
                {/* no target/download: same-window nav is the only ics path SFSafariViewController handles */}
                <AppleCalendarLink
                  href={buttonProps.icsFile}
                  aria-label="Apple Calendar"
                  title="Apple Calendar"
                  $darkMode={darkMode}
                >
                  <AppleLogo />
                </AppleCalendarLink>
                <AddToCalendarButton
                  name={buttonProps.name}
                  description={buttonProps.description}
                  startDate={buttonProps.startDate}
                  startTime={buttonProps.startTime}
                  endTime={buttonProps.endTime}
                  timeZone={buttonProps.timeZone}
                  location={buttonProps.location}
                  url={buttonProps.shareLink}
                  options="'Google','Outlook.com','Yahoo'"
                  buttonsList
                  hideTextLabelButton
                  buttonStyle="round"
                  lightMode={darkMode ? "dark" : "bodyScheme"}
                  // debug={true}
                ></AddToCalendarButton>
              </>
            );
          })()}
        </FlexRow>
      </ContentSection>
    </FlexColumn>
  );
};

export default AddToCalendar;
